import Image from 'next/image';
import { ArrowRight, Clock } from 'lucide-react';
import { Link } from '@/app/i18n/navigation';
import type { Blog } from '@/app/types/blog';
import { formatBlogDate, getCoverUrl, getReadingMinutes } from '@/app/utils/blog';

type Labels = { minRead: string; readMore: string };

const chipColors = [
  'bg-[#AFD4A940] text-[#064E3B]',
  'bg-[#FFE37B66] text-[#064E3B]',
  'bg-[#008D631A] text-[#008D63]',
  'bg-[#B91C1C14] text-[#991B1B]',
];

export function CategoryChips({ blog, max = 2 }: { blog: Blog; max?: number }) {
  const categories = (blog.category_ids ?? []).slice(0, max);
  if (!categories.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {categories.map((c, i) => (
        <span key={c.id} className={`${chipColors[i % chipColors.length]} text-xs font-bold px-3 py-1 rounded-full`}>
          {c.name ?? ''}
        </span>
      ))}
    </div>
  );
}

function Cover({ blog, sizes, priority = false }: { blog: Blog; sizes: string; priority?: boolean }) {
  const src = getCoverUrl(blog, 'cover_thumbnails_700x394');
  if (!src) {
    return (
      <div className="absolute inset-0 bg-[#A7D3AF] flex items-center justify-center">
        <Image src="/images/logo.png" alt="" width={120} height={60} className="opacity-60" />
      </div>
    );
  }
  return (
    <Image
      src={src}
      alt={blog.alt || blog.title}
      fill
      sizes={sizes}
      priority={priority}
      unoptimized
      className="object-cover transition-transform duration-500 group-hover:scale-105"
    />
  );
}

export function BlogCard({ blog, locale, labels }: { blog: Blog; locale: string; labels: Labels }) {
  return (
    <Link
      href={`/blog/${blog.slug}`}
      className="group bg-white rounded-[28px] overflow-hidden flex flex-col border-2 border-transparent hover:border-[#A7D3AF] transition-colors"
    >
      <div className="relative aspect-[16/9] overflow-hidden">
        <Cover blog={blog} sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw" />
      </div>
      <div className="p-6 flex flex-col flex-1">
        <CategoryChips blog={blog} max={1} />
        <h3 className="text-[#064E3B] text-xl font-bold leading-snug mt-3 mb-2 line-clamp-2">{blog.title}</h3>
        <p className="text-[#064E3B]/70 text-sm leading-relaxed line-clamp-3 mb-5">{blog.description}</p>
        <div className="mt-auto flex items-center justify-between text-[#064E3B]/60 text-sm">
          <span>{formatBlogDate(blog.created_at, locale)}</span>
          <span className="flex items-center gap-1.5">
            <Clock size={14} />
            {getReadingMinutes(blog.content ?? '')} {labels.minRead}
          </span>
        </div>
      </div>
    </Link>
  );
}

export function FeaturedBlogCard({ blog, locale, labels }: { blog: Blog; locale: string; labels: Labels }) {
  return (
    <Link
      href={`/blog/${blog.slug}`}
      className="group bg-white rounded-[36px] overflow-hidden grid grid-cols-1 lg:grid-cols-2 border-2 border-transparent hover:border-[#A7D3AF] transition-colors"
    >
      <div className="relative aspect-[16/9] lg:aspect-auto lg:min-h-[380px] overflow-hidden">
        <Cover blog={blog} sizes="(min-width: 1024px) 50vw, 100vw" priority />
      </div>
      <div className="p-7 md:p-10 flex flex-col justify-center">
        <CategoryChips blog={blog} />
        <h2 className="text-[#064E3B] text-2xl md:text-4xl font-bold leading-tight mt-4 mb-4">{blog.title}</h2>
        <p className="text-[#064E3B]/75 text-base md:text-lg leading-relaxed mb-6 line-clamp-4">{blog.description}</p>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[#064E3B]/60 text-sm mb-7">
          {blog.author ? <span className="font-semibold text-[#064E3B]">{blog.author}</span> : null}
          <span>{formatBlogDate(blog.created_at, locale)}</span>
          <span className="flex items-center gap-1.5">
            <Clock size={14} />
            {getReadingMinutes(blog.content ?? '')} {labels.minRead}
          </span>
        </div>
        <span className="self-start inline-flex items-center gap-2 bg-[#FFE37B] group-hover:bg-[#FACC15] text-[#064E3B] font-bold py-3 px-6 rounded-full transition-colors">
          {labels.readMore}
          <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}
